<script setup>
import { storeToRefs } from 'pinia'
import { useMessageStore } from '~/stores/messages'

// redesign (CN-12074): the app footer reuses this layout so both stay identical; the footer variant
// sits further down the page, so it must not be the page h1 and its image should load lazily
const props = defineProps({
    variant: {
        type: String,
        default: 'header',
        validator: value => ['header', 'footer'].includes(value)
    }
})

const isFooter = computed(() => props.variant === 'footer')

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Markdown
const { $md } = useNuxtApp()

// redesign (CN-12074): force the Figma line break after word two. CSS width alone can't do it
// because the two candidate phrases are identical in width, so inject a <br>. Frontend-only;
// the i18n/TYPO3 content stays untouched.
const pageHeadline = computed(() => {
    const html = $md.renderInline(staticKeys.value?.hero_app_teaser_headline_web_markdown || '')
    return html
        .replace(/^(\S+\s+\S+)\s+/, '$1<br>') // break after word two
        .replace(/\s+(<em\b)/, '<br>$1') // break before the italic accent phrase
})

const sourceAttrs = computed(() => isFooter.value ? { loading: 'lazy' } : {})
const imgAttrs = computed(() => ({
    alt: staticKeys.value?.appteaser_alt_web,
    ...(isFooter.value ? { loading: 'lazy' } : { fetchpriority: 'high' })
}))

const appButtonsConfig = computed(() => {
    return {
        iosLink: staticKeys.value?.account_gettheapp_ios_link,
        androidLink: staticKeys.value?.account_gettheapp_android_link
    }
})
</script>

<template>
    <section class="app-download-invite-header" :class="{ 'is-footer': isFooter }">
        <div class="app-download-invite-header-background" />
        <div class="app-download-invite-header-container">
            <h2 v-if="isFooter" class="app-download-invite-header-headline" v-html="pageHeadline" />
            <h1 v-else class="app-download-invite-header-headline" v-html="pageHeadline" />
            <p class="app-download-invite-header-copy">
                {{ staticKeys?.hero_app_teaser_txt_web }}
            </p>

            <AppDownloadButtonBar class="app-download-invite-header-btn-container" :app-download-button-bar-config="appButtonsConfig" />

            <div class="app-download-invite-header-media">
                <div class="app-download-invite-header-img-wrapper">
                    <div class="app-download-invite-header-img-container">
                        <picture>
                            <RespImgSrc
                                tag-type="source"
                                image-src="images/hero-goodiebag.png"
                                :srcset="[900]"
                                :format="['avif', 'webp']"
                                :width="900"
                                :height="593"
                                :quality="70"
                                sizes="(min-width: 1024px) 760px, 90vw"
                                :additional-attrs="sourceAttrs"
                            />
                            <RespImgSrc
                                tag-type="img"
                                image-src="images/hero-goodiebag.png"
                                :srcset="[900]"
                                :format="['png']"
                                :width="900"
                                :height="593"
                                :quality="70"
                                sizes="(min-width: 1024px) 760px, 90vw"
                                :additional-attrs="imgAttrs"
                            />
                        </picture>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss">
.app-download-invite-header {
    position: relative;
    margin-block-start: calc(px2rem(-73) - 36px);
    background: $app-download-invite-header-wrapper-bg;
    border-top: 1px solid $app-download-invite-header-wrapper-border;

    @include fullbleed;

    @include bp(medium1) {
        background: transparent;
        border-top: 0 none;
        margin-block-start: px2rem(-40);
    }

    @include bp(large1) {
        margin-block-start: px2rem(-60);
    }

    @include bp(767px, max) {
        overflow: hidden; // crop the oversized mobile mockup at the viewport edges
    }

    // footer variant: sits below page content instead of tucking under the header
    &.is-footer {
        margin-block-start: px2rem(40);
        overflow: hidden;

        @include bp(767px, max) {
            padding-block-end: px2rem(40); // the copy (and its bottom margin) is hidden here
        }

        @include bp(large1) {
            margin-block-start: px2rem(60);
        }
    }
}

.app-download-invite-header-background {
    overflow: hidden;
    position: absolute;
    z-index: z('stage-hero-gradient');
    inset: 0;

    @include bp(large1) {
        background: $app-download-invite-header-background;
    }

    &::before {
        content: '';
        position: absolute;
        right: 0;
        bottom: 10vh;
        max-width: 1000px;
        max-height: 1000px;
        background: radial-gradient(56.32% 74.23% at 50% 50%, $app-download-invite-gradient1 0%, $app-download-invite-gradient2 100%);
        filter: blur(50px);
        height: 50vh;
        width: 100vw;

        @include bp(medium1) {
            bottom: auto;
            width: 80vw;
            height: 80vw;
        }

        @include bp(1216px) {
            left: calc(50vw - 216px);
        }
    }
}

.app-download-invite-header-container {
    align-items: flex-start;
    position: relative;
    z-index: z('app-download-invite-container');
    grid-template-rows: auto auto auto; // mobile: headline, image (buttons overlaid), copy

    @include content-grid;

    // redesign (CN-12074): fixed Figma padding around the text (M 60/54, L 105) instead of letting the
    // fluid mockup drive the height, otherwise the text-to-edge spacing drifts while resizing
    @include bp(medium1) {
        grid-template-rows: auto auto auto;
        padding-block: px2rem(60) px2rem(54);
    }

    @include bp(large1) {
        padding-block: px2rem(105);
    }
}

.app-download-invite-header-headline {
    text-align: left;
    grid-column: 1 / -1;
    grid-row: 1;

    @include app-headline($margin: 90px 0 44px);

    @include bp(767px, max) {
        font-size: px2rem(36); // redesign (CN-12074): Figma S hero headline is 36/42 (app-headline base is 32/38)
        line-height: px2rem(42);
        margin: px2rem(108) 0 px2rem(8); // Figma S: ~40px below header, ~24px to the image
    }

    @include bp(medium1) {
        margin: 0 0 16px;
        grid-row: 1;
        grid-column: 1/ span 4;
    }

    @include bp(large1) {
        margin: 0;
        grid-column: 1/ span 5;
    }

    @include bp(large2) {
        grid-column: 1/ span 4;
    }

    @include bp(large3) {
        line-height: px2rem(64); // redesign (CN-12074): Figma D1 Large line-height is 64 (app-headline mixin sets 56)
    }

    .is-footer & {
        @include bp(767px, max) {
            margin-block-start: px2rem(40); // the 108px of the hero only compensates its pull under the header
        }
    }
}

.app-download-invite-header-copy {
    margin: 14px 0 40px;
    grid-column: 1/ span 5;
    grid-row: 4;

    @include bp(767px, max) {
        grid-row: 3; // directly below the image, buttons are overlaid on the image above
        margin: px2rem(8) 0 px2rem(40);
    }

    .is-footer & {
        @include bp(767px, max) {
            display: none;
        }
    }

    @include body1-medium;

    @include bp(small3) {
        grid-column: 1/ span 4;
    }

    @include bp(medium1) {
        grid-column: 1/ span 4;
        grid-row: 2;
        margin: 0 0 24px;
    }

    @include bp(large1) {
        grid-column: 1/ span 5;
        margin: 0 0 10px;
    }

    @include bp(large2) {
        grid-column: 1/ span 4;
    }
}

.app-download-invite-header-img-container {
    position: relative;
    width: 100%;

    @include bp(767px, max) {
        width: 130%; // Figma S: mockup is larger than the viewport and cropped at the sides
        margin-inline: -15%;
    }

    // redesign (CN-12074): let the mockup bleed out of the content into the right gutter from tablet up,
    // so it reaches the Figma size instead of being capped at its grid cell
    @include bp(medium1) {
        width: calc(100% + 8vw);
        margin-inline-end: -8vw;
    }

    img {
        display: block;
        max-width: 100%;
        width: 100%;
        height: auto;
    }
}

.app-download-invite-header-btn-container {
    display: flex;
    align-items: center;
    flex-flow: row wrap;
    margin: 0 0 8px;
    grid-column: 1/ span 2;
    grid-row: 3;

    @include bp(767px, max) {
        position: relative; // stack above the mockup image
        z-index: 1;
        grid-row: 2; // overlay the lower-left of the mockup like Figma S
        place-self: end start;
        margin: 0;
    }

    @include bp(medium1) {
        margin: 0;
        grid-column: 1/ span 4;
        grid-row: 3;
    }

    @include bp(large1) {
        grid-column: 1/ span 5;
    }

    @include bp(large2) {
        grid-column: 1/ span 4;
    }

    a {
        display: block;
        line-height: 0;
        margin-block-end: px2rem(8); // Figma S: ~8px between the stacked app badges

        @include bp(medium1) {
            max-width: 140px;
            margin-block-end: px2rem(16);
        }

        img {
            width: 120px;
            height: auto;

            @include bp(medium1) {
                width: 140px;
            }
        }
    }
}

.app-download-invite-header-media {
    grid-column: 1 / -1;
    grid-row: 2;
    display: flex;
    justify-content: center;

    @include bp(medium1) {
        grid-column: 4 / -1; // Figma M: mockup is larger and starts further left
        position: absolute; // out of the height calculation; negative inset reaches into the padding so it centers on the whole hero
        inset: px2rem(-60) 0 px2rem(-54);
        grid-row: 1 / -1;
        align-items: center;
        justify-content: flex-end;
    }

    @include bp(large1) {
        grid-column: 6 / span 7;
        inset: px2rem(-105) 0;
    }
}

.app-download-invite-header-img-wrapper {
    margin: 0;
    width: 100%;
    display: flex;
    justify-content: center;

    // no vertical auto margins: they collapse to 0 when the mockup is taller than the text and pin it to the top
    @include bp(medium1) {
        margin: 0 -4vw;
        width: calc(100% + 4vw);
        justify-content: flex-end;
    }

    @include bp(large1) {
        margin: 0;
    }
}
</style>
